import type { ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import { ARABIAN_SEA, MUMBAI_DECO, MUMBAI_GOTHIC, darken } from '../palettes';

/**
 * scenery/zones/mumbai.ts — golden monsoon haze. The Victorian-Gothic Fort
 * (red double-deckers, black-and-yellow kaali-peeli taxis) → the long Marine
 * Drive sweep beside the Arabian Sea → the Haji Ali causeway → grand Colaba
 * to the Gateway. The sea lies on side +1.
 */

export const SEA_SIDE = 1;
// KAALI_PEELI 0x1c1d22 retired with the cab pushes (mumbai-ts) — see the
// dropped-draw comments in `fort` and `colaba` below.

const fort: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, progress, marks }) => {
  const cstAt = marks.cst;
  if (cstAt != null && Math.abs(progress - cstAt) < 0.022) return; // forecourt for the terminus
  // Sober Gothic civic blocks: dark basalt with cream trim bands.
  if (rng.next() < 0.88) {
    const w = rng.range(10, 16);
    const h = rng.range(9, 18);
    const d = rng.range(10, 15);
    const lat = hw + 2.5 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    const color = rng.pick(MUMBAI_GOTHIC);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 1, h: 1.1, d: d + 1, color: 0xd8ccae });
    a.plain.push({ x, y: y + h * 0.55, z, rotY: s.dir, w: w + 0.5, h: 0.5, d: d + 0.5, color: 0xd8ccae });
  }
  // Red double-deckers working the kerb — BEST livery, and a BEST route board
  // (mumbai-nits: they wore the London fleet's "23 · WESTMINSTER" blind).
  if (rng.next() < 0.1) {
    const [x, y, z] = at(hw + 3 + rng.range(0, 1.2));
    a.buses.push({ x, y, z, rotY: s.dir, color: 0xc81f2a, sign: { route: '1', dest: 'COLABA' } });
  }
  // Kaali-peeli taxis DROPPED (mumbai-ts): behind the guardrail only the
  // cab's featureless yellow cabin box peeked over the wall cap — it read as
  // a floating yellow crate at eye level (measured, QA colaba-chase). The rng
  // draws are kept verbatim so every later placement stays byte-identical;
  // re-add the push once buildCabs gets a readability pass (GS4b bus style).
  if (rng.next() < 0.2) rng.range(0, 1.2);
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.5, hw + 4.5], chance: 0.5, count: 3 });
};

const marinedrive: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === SEA_SIDE) {
    // The sea wall, then the monsoon-green Arabian Sea to the horizon.
    const [wx, wy, wz] = at(hw + 1.3);
    a.plain.push({ x: wx, y: wy, z: wz, rotY: s.dir, w: 1.8, h: 1.6, d: step + 1, color: 0xc8bea8 });
    {
      const w = rng.range(60, 80);
      const [x, y, z] = at(hw + 4 + w / 2);
      a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: ARABIAN_SEA, arc: s.distance, side, shore: hw + 4 });
    }
  } else {
    // The art-deco strip: rounded pastel apartments with cream floor bands.
    if (rng.next() < 0.86) {
      const w = rng.range(9, 14);
      const h = rng.range(10, 18);
      const d = rng.range(9, 13);
      const lat = hw + 2.5 + w / 2 + rng.range(0, 3);
      const [x, y, z] = at(lat);
      const color = rng.pick(MUMBAI_DECO);
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
      a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 0.7, h: 0.8, d: d + 0.7, color: darken(color, 0.72) });
      // The Marine Drive signatures (M14, reshaped mumbai-ts): the stepped
      // ART-DECO crown — steps now SUNK into the massing below them (the old
      // roof-plane-kissing slabs read as loose planks from any elevated
      // angle) — a flush tapering FIN BLADE on the street face rising past
      // the parapet (was a 0.3×1.3 stick jutting off the corner), and the
      // wrap-around eyebrow ledges. The Queen's Necklace look. Geometry-only
      // reshape: no rng draw added or removed.
      a.plain.push({ x, y: y + h + 0.5, z, rotY: s.dir, w: w * 0.6, h: 2.2, d: d * 0.6, color }); // crown step 1, sunk 0.6 into the roof
      a.plain.push({ x, y: y + h + 1.9, z, rotY: s.dir, w: w * 0.32, h: 1.8, d: d * 0.32, color: 0xf2ead8 }); // crown step 2, sunk into step 1
      const [fx2, fy2, fz2] = at(lat - w / 2 + 0.02); // blade centre ~flush with the face
      a.plain.push({ x: fx2, y: fy2 + h * 0.34, z: fz2, rotY: s.dir, w: 0.44, h: h * 0.68, d: 3.0, color: 0xf2ead8 }); // fin: wide shoulder panel
      a.plain.push({ x: fx2, y: fy2 + h * 0.78, z: fz2, rotY: s.dir, w: 0.38, h: h * 0.44 + 1.4, d: 1.7, color: 0xf2ead8 }); // fin: top step past the roofline
      for (const fy of [0.3, 0.62]) {
        a.plain.push({ x, y: y + h * fy, z, rotY: s.dir, w: w + 0.9, h: 0.22, d: d + 0.9, color: 0xf2ead8 }); // eyebrow ledges
      }
    }
    if (rng.next() < 0.3) {
      const [x, y, z] = at(hw + 2.2);
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.25) });
    }
  }
  // The Queen's Necklace: people up at the sea wall; strollers on the deco walk.
  if (side === SEA_SIDE)
    scatterPeople({ peopleRng, at, a }, { band: [hw + 0.8, hw + 1.3], chance: 0.55, count: 3 });
  else scatterPeople({ peopleRng, at, a }, { band: [hw + 1.7, hw + 4.5], chance: 0.5, count: 3 });
};

const hajiali: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === SEA_SIDE) {
    // Open water — the dargah landmark stands out in it.
    const w = rng.range(60, 80);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: ARABIAN_SEA, arc: s.distance, side, shore: hw + 1 });
  } else {
    // A low city edge holding back from the water.
    if (rng.next() < 0.55) {
      const w = rng.range(9, 14);
      const h = rng.range(6, 12);
      const [x, y, z] = at(hw + 5 + w / 2 + rng.range(0, 5));
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(9, 13), color: rng.pick(MUMBAI_DECO) });
    }
    if (rng.next() < 0.2) {
      const [x, y, z] = at(hw + 2.3);
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.85, 1.15) });
    }
    scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 4.5], chance: 0.35, count: 2 });
  }
};

const colaba: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, progress, marks }) => {
  // Keep the Taj + Gateway forecourts clear.
  const tajAt = marks.tajHotel;
  if (tajAt != null && Math.abs(progress - tajAt) < 0.025 && side === -1) return;
  const gateAt = marks.gatewayOfIndia;
  if (gateAt != null && Math.abs(progress - gateAt) < 0.02) return;
  // Grand stone hotels + arcaded blocks.
  if (rng.next() < 0.8) {
    const w = rng.range(11, 16);
    const h = rng.range(10, 17);
    const d = rng.range(10, 15);
    const lat = hw + 2.5 + w / 2 + rng.range(0, 4);
    const [x, y, z] = at(lat);
    const color = rng.pick([...MUMBAI_DECO, 0xc89a6a]);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 0.9, h: 1, d: d + 0.9, color: darken(color, 0.7) });
  }
  // Kaali-peeli taxis DROPPED here too (mumbai-ts, same floating-crate read —
  // the QA finding's "~0.85 Colaba" site). Draws kept for layout determinism.
  if (rng.next() < 0.16) rng.range(0, 1);
  // Tourists and hawkers around the Gateway and the grand Colaba hotels.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 5], chance: 0.55, count: 3 });
};

export const MUMBAI_ZONES = { fort, marinedrive, hajiali, colaba };
